import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { errorToast } from 'basics/utils/toast';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export type ExportQrCodeResponse = {
  exportQrCode: {
    buffer: string;
  };
};

export type UseExportQrCodeMutation = [
  (eventId?: string, email?: string) => Promise<string | null>,
  MutationResult<ExportQrCodeResponse>,
];

export const exportQrCodeMutationCall = gql`
mutation exportQrCode($eventId: String!, $email: String!) {
    exportQrCode(eventId: $eventId, email: $email) {
      buffer
    }
}`;

export const useExportQrCodeMutation = (): UseExportQrCodeMutation => {
  const { t } = useTranslation();
  const [exportQrCodeMutation, state] = useMutation<ExportQrCodeResponse>(exportQrCodeMutationCall);

  const executeExportQrCodeMutation = useCallback(async (eventId?: string, email?: string) => {
    try {
      if (!eventId || !email) {
        errorToast(t('errors_standard'));
        return null;
      }
      const { data } = await exportQrCodeMutation({
        variables: { eventId, email },
      });

      return data?.exportQrCode?.buffer || null;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'exportQrCode.ts - useExportQrCodeMutation',
        });
        errorToast(t('qr_codes_generation_error'));
      }
      return null;
    }
  }, [exportQrCodeMutation, t]);

  return [executeExportQrCodeMutation, state];
};
